/**
 * Historique des modifications d'un agent IA : qui, quand, quels champs, et
 * « Revenir à avant » pour défaire une modification (la restauration est
 * elle-même une entrée, donc défaisable). Alimenté par le dashboard comme par
 * l'API et le MCP.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { History, Loader2, Undo2 } from 'lucide-react'
import { toast } from 'sonner'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAIAgentRevisions, useRestoreAIAgentRevision, type AIAgentRevision } from '@/api/aiAgents'

const NOMS: Record<string, string> = {
  name: 'Nom', first_message: 'Message d’accueil', system_prompt: 'Consignes', business_context: 'Contexte',
  language: 'Langue', voice_id: 'Voix', is_active: 'Actif', sms_actions: 'Textos', transfer_actions: 'Transferts',
  notify_admin_actions: 'Notifications', knowledge_text: 'Connaissances', knowledge_web_urls: 'Pages web',
  lookup_table_ids: 'Tables de référence',
}

function apercu(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'oui' : 'non'
  if (typeof v === 'string') return v.length > 140 ? v.slice(0, 140) + '…' : v
  if (Array.isArray(v)) return v.length ? `${v.length} élément(s)` : 'aucun'
  if (typeof v === 'object' && 'enabled' in (v as object)) {
    const p = v as { title?: string; enabled?: boolean; content?: string }
    return `${p.title || ''} · ${p.enabled ? 'active' : 'désactivée'} · ${(p.content || '').length} car.`
  }
  return JSON.stringify(v).slice(0, 140)
}

/** Aperçu sur une ligne pour la liste repliée. */
function court(v: unknown): string {
  const t = apercu(v).replace(/\s+/g, ' ')
  return t.length > 60 ? t.slice(0, 60) + '…' : t
}

export function AgentRevisionsSheet({ companyId, agentId, open, onOpenChange, onRestored }: {
  companyId: number | undefined; agentId: number | undefined; open: boolean; onOpenChange: (o: boolean) => void; onRestored?: () => void
}) {
  const { t, i18n } = useTranslation()
  const { data: revisions, isLoading } = useAIAgentRevisions(companyId, agentId, open)
  const restaurer = useRestoreAIAgentRevision(companyId, agentId)
  const [ouverte, setOuverte] = useState<number | null>(null)

  const nomChamp = (c: string) => c.startsWith('knowledge_page_') ? t('manager.aiAgents.revisions.fields.knowledgePage', 'Page de connaissances') : t(`manager.aiAgents.revisions.fields.${c}`, NOMS[c] || c)
  const source = (r: AIAgentRevision) =>
    r.source === 'api' ? t('manager.aiAgents.revisions.viaApi', 'par l’API') : r.source === 'restore' ? t('manager.aiAgents.revisions.viaRestore', 'retour en arrière') : t('manager.aiAgents.revisions.viaDashboard', 'dans le dashboard')

  const revenir = async (r: AIAgentRevision) => {
    try {
      await restaurer.mutateAsync(r.id)
      toast.success(t('manager.aiAgents.revisions.restored', 'Agent remis dans l’état d’avant.'))
      onRestored?.()
    } catch {
      toast.error(t('manager.aiAgents.revisions.restoreError', 'Impossible de revenir en arrière.'))
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-6 pt-6 pb-4 border-b dark:border-slate-800 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg"><History className="h-5 w-5 text-blue-600" /></div>
                {t('manager.aiAgents.revisions.title', 'Historique des modifications')}
              </SheetTitle>
            </SheetHeader>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : !revisions?.length ? (
              <p className="px-6 py-12 text-center text-sm text-slate-500">{t('manager.aiAgents.revisions.empty', 'Aucune modification enregistrée pour l’instant.')}</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {revisions.map((r) => {
                  const est = ouverte === r.id
                  return (
                    <li key={r.id} className="px-6 py-4">
                      <button type="button" onClick={() => setOuverte(est ? null : r.id)} className="w-full text-left">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {r.changed_fields.map(nomChamp).join(', ')}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {new Date(r.created_at).toLocaleString(i18n.language)} · {source(r)}{r.author ? ` · ${r.author}` : ''}
                        </p>
                        {!est && (
                          <p className="text-xs text-slate-600 dark:text-slate-300 mt-1.5 truncate">
                            <span className="text-slate-400 line-through decoration-slate-300">{court(r.before[r.changed_fields[0]])}</span>
                            <span className="mx-1.5 text-slate-400">→</span>
                            <span>{court(r.after[r.changed_fields[0]])}</span>
                          </p>
                        )}
                      </button>
                      {est && (
                        <div className="mt-3 space-y-3">
                          {r.changed_fields.map((c) => (
                            <div key={c} className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden text-xs">
                              <p className="px-3 py-1.5 bg-slate-50 dark:bg-slate-800/60 font-medium text-slate-700 dark:text-slate-200">{nomChamp(c)}</p>
                              <div className="grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-800">
                                <div className="px-3 py-2">
                                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{t('manager.aiAgents.revisions.before', 'Avant')}</p>
                                  <p className={cn('whitespace-pre-wrap break-words', 'text-slate-600 dark:text-slate-300')}>{apercu(r.before[c])}</p>
                                </div>
                                <div className="px-3 py-2">
                                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{t('manager.aiAgents.revisions.after', 'Après')}</p>
                                  <p className="whitespace-pre-wrap break-words text-slate-900 dark:text-white">{apercu(r.after[c])}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                          <Button type="button" variant="outline" size="sm" disabled={restaurer.isPending} onClick={() => revenir(r)}>
                            <Undo2 className="h-3.5 w-3.5 mr-1.5" />{t('manager.aiAgents.revisions.restore', 'Revenir à avant cette modification')}
                          </Button>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
